<template>
  <header
    :class="b({ mobileNavigationOpen })"
    :style="{ '--c-header__user-section-height': userSectionHeight }"
  >
    <div
      ref="userSection"
      :class="b('user-section')"
    >
      <div :class="b('user-section-inner')">
        <div :class="b('username')">
          <template v-if="sessionStore.getIsLoggedIn && fullUserName">
            {{ $t('c-header.userGreeting', { name: fullUserName }) }}
          </template>
        </div>
        <c-user-navigation v-if="!$viewport.isTablet" />
      </div>
    </div>
    <div :class="b('content')">
      <a
        :class="b('logo-wrapper', { large: sessionStore.logoUseWideSize, hasNoCartIcon: !cartAmount })"
        href="/"
      >
        <img
          :class="b('logo', { large: sessionStore.logoUseWideSize, hasNoCartIcon: !cartAmount })"
          :alt="$t('c-header.logoAlt')"
          :src="sessionStore.logoUrl"
          width="284"
          height="151"
        />
      </a>
      <div
        v-if="!$viewport.isTablet"
        :class="b('navigation')"
      >
        <c-main-navigation />
      </div>
      <ul :class="b('meta-navigation')">
        <li :class="b('meta-navigation-item', { search: true })">
          <c-search-bar
            ref="search"
            @focus="onSearchFocus"
            @blur="onSearchBlur"
          />
        </li>
        <li :class="b('meta-navigation-item', { language: true })">
          <c-language-menu />
        </li>
        <li
          v-if="sessionStore.isCartEnabled"
          :class="[b('meta-navigation-item', { cart: true }), !cartAmount ? 'is-visible--desktop' : '']"
        >
          <a
            :href="$api.getUrl('shop-cart')"
            :title="$t('c-header.cartTitle')"
            :class="b('cart-link')"
          >
            <e-icon-sprite
              :alt="$t('c-header.cartAlt')"
              icon="i-cart"
              size="24"
            />
            <span
              v-if="cartAmount"
              :class="b('cart-amount-badge', { circle: cartAmount <= 99 })"
            >
              {{ cartAmount }}
            </span>
          </a>
        </li>
        <li
          v-if="$viewport.isTablet"
          :class="b('meta-navigation-item', { menu: true })"
        >
          <button
            :class="b('burger')"
            :aria-label="$t('c-header.menuAlt')"
            type="button"
            @click="mobileNavigationOpen = !mobileNavigationOpen"
          >
            <span :class="b('burger-line')"></span>
            <span :class="b('burger-line')"></span>
            <span :class="b('burger-line')"></span>
          </button>
        </li>
      </ul>
    </div>
    <div
      v-if="$viewport.isTablet"
      :class="b('mobile-navigation')"
    >
      <c-main-navigation-mobile :open="mobileNavigationOpen" />
    </div>
    <c-notification-container />
    <c-market-selector v-if="sessionStore.showMarketSelector" />
    <c-course-booking v-if="!!coursesStore.bookingCourse" />
    <c-badge-level-achievement v-if="sessionStore.showBadgeLevelAchievement && !!sessionStore.getBadgeLevelForUser" />
    <c-confirmation-modal />
    <portal-target
      name="modal-container"
      multiple
    />
    <portal-target
      name="flyout-container"
      multiple
    />
    <portal-target
      name="tooltip"
      multiple
    />
    <c-global-loader />
  </header>
</template>

<script>
  import { PortalTarget } from 'portal-vue';
  import useCoursesStore from '@/stores/courses';
  import useSessionStore from '@/stores/session';
  import cBadgeLevelAchievement from '@/components/c-badge-level-achievement.vue';
  import cCourseBooking from '@/components/c-course-booking.vue';
  import cGlobalLoader from '@/components/c-global-loader.vue';
  import cLanguageMenu from '@/components/c-language-menu.vue';
  import cMainNavigationMobile from '@/components/c-main-navigation-mobile.vue';
  import cMainNavigation from '@/components/c-main-navigation.vue';
  import cMarketSelector from '@/components/c-market-selector.vue';
  import cNotificationContainer from '@/components/c-notification-container.vue';
  import cSearchBar from '@/components/c-search-bar.vue';
  import cUserNavigation from '@/components/c-user-navigation.vue';
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import cConfirmationModal from '@/plugins/confirmation-modal/c-confirmation-modal.vue';

  /**
   * Renders the page header.
   */
  export default {
    name: 'c-header',
    components: {
      eIconSprite,
      portalTarget: PortalTarget,
      cConfirmationModal,
      cBadgeLevelAchievement,
      cCourseBooking,
      cMarketSelector,
      cLanguageMenu,
      cSearchBar,
      cMainNavigation,
      cMainNavigationMobile,
      cNotificationContainer,
      cUserNavigation,
      cGlobalLoader,
    },
    // mixins: [],

    // props: {},
    setup() {
      return {
        sessionStore: useSessionStore(),
        coursesStore: useCoursesStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} Mobile navigation open state.
         */
        mobileNavigationOpen: false,

        /**
         * Holds the height of the user section to modify the fixed position.
         */
        userSectionHeight: 0,

        /**
         * @type {Number} Holds the calculated width for the open search field.
         */
        openSearchWidth: null,
      };
    },

    computed: {
      /**
       * Maps the full user name (firstName and lastName).
       *
       * @returns {String|null}
       */
      fullUserName() {
        const { firstName, lastName } = this.sessionStore.user || {};

        return firstName && lastName ? `${firstName} ${lastName}` : null;
      },

      /**
       *  Returns the amount of products in the cart.
       *
       *  @returns {String|null}
       */
      cartAmount() {
        const getCartAmount = Number(this.sessionStore.cartAmount);

        if (!getCartAmount || Number.isNaN(getCartAmount)) {
          return null;
        }

        return `${getCartAmount > 999 ? '999+' : getCartAmount}`;
      },
    },
    watch: {
      /**
       * Reset mobile navigation after switching to desktop.
       */
      '$viewport.isMd': function () {
        this.mobileNavigationOpen = false;
      },
    },

    // beforeCreate() {},
    created() {
      this.sessionStore.apiGetCartSummary();
    },
    // beforeMount() {},
    mounted() {
      this.updateHeightDefinition();

      window.addEventListener('resizeend', this.updateHeightDefinition);
      window.addEventListener('resizeend', this.updateSearchFieldWidth);
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      window.removeEventListener('resizeend', this.updateHeightDefinition);
      window.removeEventListener('resizeend', this.updateSearchFieldWidth);
    },
    // unmounted() {},

    methods: {
      /**
       * Recalculates the height of the user section and updates the current value.
       */
      updateHeightDefinition() {
        this.userSectionHeight = `${this.$refs.userSection.offsetHeight * -1}px`;

        document.body.style.setProperty('--header-height', `${this.$el.offsetHeight}px`);
      },

      /**
       * Update calculated search field width.
       */
      updateSearchFieldWidth() {
        const { search } = this.$refs;
        const searchField = search?.$el;
        const flyout = search?.$refs.flyout?.$refs.flyout;

        if (searchField && flyout) {
          const posRight = window.innerWidth - (searchField.offsetWidth + searchField.offsetLeft);

          this.openSearchWidth = flyout.offsetWidth - posRight;
        } else {
          this.openSearchWidth = null;
        }
      },

      /**
       * Focus event handler for the search field.
       */
      onSearchFocus() {
        setTimeout(() => {
          this.updateSearchFieldWidth();
        });
      },

      /**
       * Blur event handler for the search field.
       */
      onSearchBlur() {
        this.openSearchWidth = null;
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  c-header {
    height: functions.rem(64px);

    @include mixins.media(md) {
      height: functions.rem(150px);
    }

    @include mixins.media(xl) {
      height: functions.rem(150px);
    }
  }

  // SSR specific styles.
  .vue-ssr-content {
    .c-header__meta-navigation-item--cart,
    .c-header__meta-navigation-item--search {
      display: none;

      @include mixins.media(md) {
        display: flex;
      }
    }

    .c-header__meta-navigation-item--menu {
      @include mixins.media(md) {
        display: none;
      }
    }
  }

  .c-header {
    $this: &;

    @include mixins.z-index(navigation);

    position: sticky;
    top: var(--c-header__user-section-height, 0); // Required by position: sticky.
    flex: 0 0 auto;
    background-color: variables.$color-grayscale--1000;

    @media print {
      display: none;
    }

    @include mixins.media(md) {
      border-bottom: 1px solid variables.$color-grayscale--900;
    }

    &::before {
      @include mixins.media($down: sm) {
        @include mixins.z-index(headerShadow);

        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        content: '';
        box-shadow: 0 2px 4px 0 rgba(variables.$color-grayscale--0, 0.09);
        pointer-events: none;
      }
    }

    &__user-section {
      display: none;
      border-bottom: 1px solid variables.$color-grayscale--900;
      min-height: 45px;

      @include mixins.media(md) {
        display: block;
      }
    }

    &__user-section-inner {
      @extend %layout;

      display: flex;
      justify-content: space-between;
      align-items: center;
      max-width: functions.rem(map.get(variables.$breakpoints, lg) - (variables.$spacing--60 * 2));
      padding-right: functions.rem(variables.$spacing--25);
      padding-left: functions.rem(variables.$spacing--25);
      color: variables.$color-primary--1;

      @include mixins.media(md) {
        max-width: functions.rem(map.get(variables.$breakpoints, lg), variables.$font-size--lg-scale-basis);
        padding-right: functions.rem(variables.$spacing--80);
        padding-left: functions.rem(variables.$spacing--80);
      }
    }

    &__content {
      @extend %layout;
      @include mixins.font(variables.$font-size--14);

      display: flex;
      align-items: center;
      max-width: functions.rem(map.get(variables.$breakpoints, lg) - (variables.$spacing--60 * 2));
      padding: functions.rem(16px 26px); // Custom value to align with eCommerce

      @include mixins.media(md) {
        @include mixins.font(variables.$font-size--18);

        max-width: none;
        padding: functions.rem(variables.$spacing--15 variables.$spacing--80);
      }

      @include mixins.media(lg) {
        @include mixins.font(variables.$font-size--18);

        max-width: functions.rem(map.get(variables.$breakpoints, lg), variables.$font-size--lg-scale-basis);
      }
    }

    &__logo-wrapper {
      display: block;
      flex: 0 1 functions.rem(60px);
      margin-right: functions.rem(variables.$spacing--20);

      @include mixins.media(sm) {
        flex: 0 0 functions.rem(120px);
        margin-right: functions.rem(variables.$spacing--80);
      }

      &--large {
        flex: 1 0 functions.rem(80px);

        @include mixins.media(sm) {
          flex: 0 0 functions.rem(208px);
        }
      }
    }

    #{$this}__logo-wrapper--large {
      @include mixins.media($down: sm) {
        &#{$this}__logo-wrapper--has-no-cart-icon {
          flex: 1 0 functions.rem(120px);
        }
      }
    }

    &__logo {
      width: 100%;
      max-width: functions.rem(60px);
      height: auto;

      @include mixins.media(md) {
        max-width: functions.rem(120px);
      }

      @include mixins.media(lg) {
        max-width: functions.rem(100px, variables.$font-size--lg-scale-basis);
      }

      &--large {
        max-width: functions.rem(150px);

        @include mixins.media(md) {
          max-width: functions.rem(200px);
        }

        @include mixins.media(lg) {
          max-width: functions.rem(208px, variables.$font-size--lg-scale-basis);
        }
      }
    }

    &__navigation {
      display: none;
      flex: 1 1 0%;

      @include mixins.media(md) {
        display: block;
      }
    }

    &__meta-navigation {
      @extend %list-reset;

      display: flex;
      flex: 0 0 auto;
      justify-content: flex-end;
      align-items: center;
      margin-left: auto;
      gap: functions.rem(variables.$spacing--25);

      @include mixins.media(md) {
        margin-left: variables.$spacing--80;
        gap: functions.rem(variables.$spacing--40);
      }
    }

    &__meta-navigation-item--menu {
      display: flex;
      align-items: flex-end;
    }

    &__meta-navigation-item--search {
      max-width: functions.rem(240px);

      @include mixins.media(md) {
        .c-search-bar {
          position: relative;
          left: 0;
          width: 100%;
          transition:
            width variables.$transition-duration--600 ease-in-out,
            left variables.$transition-duration--600 ease-in-out;

          &::before {
            $background-gradient: linear-gradient(
              -90deg,
              rgba(variables.$color-grayscale--1000, 1) functions.rem(10px),
              rgba(variables.$color-grayscale--1000, 0) 100%
            );

            position: absolute;
            top: 0;
            right: calc(100% - #{functions.rem(10px)});
            bottom: 0;
            display: block;
            content: '';
            width: functions.rem(100px);
            background: $background-gradient 0 0 no-repeat;
          }
        }

        .c-search-bar--has-focus {
          // Only fallback.
          left: calc(100% - #{functions.rem(455px)});
          width: functions.rem(455px);
        }
      }
    }

    &__burger {
      @extend %button-reset;

      width: functions.rem(variables.$spacing--25);
      overflow: hidden; // Cut oversized burger-lines.
    }

    &__burger-line {
      display: block;
      width: 120%; // The additional width is needed when the items are rotated by 45deg.
      height: 1px;
      margin-bottom: functions.rem(8.75px);
      background-color: variables.$color-primary--1;
      transform-origin: left top;
      transition: all 0.3s ease-in-out;

      &:nth-child(3) {
        margin-bottom: 0;
        transform-origin: left bottom;
      }
    }

    &--mobile-navigation-open {
      #{$this}__burger-line {
        &:nth-child(1) {
          transform: rotate(45deg);
        }

        &:nth-child(2) {
          opacity: 0;
        }

        &:nth-child(3) {
          transform: rotate(-45deg);
        }
      }
    }

    &__search {
      width: functions.rem(230px);

      .e-search__field {
        &::-webkit-input-placeholder {
          // WebKit, Blink, Edge
          color: variables.$color-primary--1;
        }

        &:-moz-placeholder {
          // Mozilla Firefox 4 to 18
          color: variables.$color-primary--1;
        }

        &::placeholder {
          // Most modern browsers support this now
          color: variables.$color-primary--1;
        }
      }
    }

    &__cart-link {
      position: relative;
      display: block;
      color: variables.$color-primary--1;

      &:hover {
        color: variables.$color-primary--2;
      }

      .e-icon-sprite {
        width: functions.rem(24px);
        height: functions.rem(24px);
      }
    }

    &__cart-amount-badge {
      @include mixins.font(variables.$font-size--12, null, variables.$font-weight--medium);

      position: absolute;
      top: functions.rem(-12px);
      right: functions.rem(-23px);
      display: flex;
      justify-content: center;
      align-items: center;
      height: functions.rem(20px);
      padding-top: functions.rem(variables.$spacing--5); // Improves vertical alignment
      border-radius: functions.rem(25px);
      background-color: variables.$color-primary--1;
      color: variables.$color-grayscale--1000;
      padding-inline: functions.rem(6px);

      @include mixins.media(sm) {
        right: functions.rem(-25px);
        padding-top: functions.rem(variables.$spacing--2); // Improves vertical alignment
      }

      &--circle {
        right: functions.rem(-12px);
        width: functions.rem(20px);
        border-radius: 50%;
        padding-inline: 0;
      }
    }

    .e-search__field {
      @include mixins.media(lg) {
        @include mixins.font(variables.$font-size--16);
      }
    }
  }

  body.no-scroll {
    // Fixes cut of borders when scroll on body is disabled.
    .c-header__user-section::after,
    .c-header::after {
      position: absolute;
      left: 100%;
      content: '';
      width: 50px;
      border-bottom: 1px solid variables.$color-grayscale--900;
    }
  }
</style>
